/**
 * LogsFilterBar Component
 * Activity Logs list filters only (search, user, status, category, results).
 * Date range lives in the shared page header with Analytics.
 */

import React, { useMemo } from 'react';
import {
  Search,
  X,
  AlertCircle,
} from 'lucide-react';
import {
  Button,
  FilterDropdown,
  SearchDropdownMultiSelect,
  SearchDropdownSingleSelect,
} from 'lift-ui';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  LogsFilterState,
  LogSeverity,
  ActionCategory,
  CATEGORY_CONFIGS,
  SEVERITY_CONFIGS,
} from '../../types';

const SEVERITY_OPTIONS = (Object.keys(SEVERITY_CONFIGS) as LogSeverity[]).map(
  (severity) => ({
    id: severity,
    label: SEVERITY_CONFIGS[severity].label,
  })
);

const CATEGORY_OPTIONS = CATEGORY_CONFIGS.map((category) => ({
  id: category.id,
  label: category.label,
}));

const RESULT_MODE_OPTIONS = [
  { id: 'all', label: 'All results' },
  { id: 'errors', label: 'Errors only' },
];

interface LogsFilterBarProps {
  filters: LogsFilterState;
  users: Array<{ id: number; name: string; email: string }>;
  onFilterChange: (filters: Partial<LogsFilterState>) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  totalResults: number;
  hasMore?: boolean;
}

export function LogsFilterBar({
  filters,
  users,
  onFilterChange,
  onReset,
  hasActiveFilters,
  totalResults,
  hasMore = false,
}: LogsFilterBarProps) {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ searchText: e.target.value });
  };

  const handleSeverityToggle = (severity: LogSeverity) => {
    const current = filters.severity;
    const updated = current.includes(severity)
      ? current.filter((s) => s !== severity)
      : [...current, severity];
    onFilterChange({ severity: updated });
  };

  const handleCategoryToggle = (category: ActionCategory) => {
    const current = filters.categories;
    const updated = current.includes(category)
      ? current.filter((c) => c !== category)
      : [...current, category];
    onFilterChange({ categories: updated });
  };

  const handleSeverityChange = (selectedIds: string[]) => {
    onFilterChange({ severity: selectedIds as LogSeverity[] });
  };

  const handleCategoryChange = (selectedIds: string[]) => {
    onFilterChange({ categories: selectedIds as ActionCategory[] });
  };

  const handleUserChange = (selectedIds: string[]) => {
    const ids = selectedIds
      .map((id) => parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));
    onFilterChange({ users: ids });
  };

  const handleResultModeChange = (selectedId: string) => {
    onFilterChange({ showOnlyErrors: selectedId === 'errors' });
  };

  const userDropdownItems = useMemo(
    () =>
      users.map((user) => ({
        id: String(user.id),
        label: user.email ? `${user.name} (${user.email})` : user.name,
      })),
    [users]
  );

  const selectedUserIds = useMemo(
    () => filters.users.map(String),
    [filters.users]
  );

  const userTriggerLabel = useMemo(() => {
    if (filters.users.length === 0) return 'All Users';
    if (filters.users.length === 1) {
      return users.find((u) => u.id === filters.users[0])?.name || '1 user';
    }
    return `${filters.users.length} users`;
  }, [filters.users, users]);

  // FilterDropdown prefixes "Status:" / "Category:" — keep values short
  const statusTriggerLabel = useMemo(() => {
    if (filters.severity.length === 0) return 'All';
    if (filters.severity.length === 1) {
      return SEVERITY_CONFIGS[filters.severity[0]]?.label || '1';
    }
    return `${filters.severity.length} selected`;
  }, [filters.severity]);

  const categoryTriggerLabel = useMemo(() => {
    if (filters.categories.length === 0) return 'All';
    if (filters.categories.length === 1) {
      return (
        CATEGORY_CONFIGS.find((c) => c.id === filters.categories[0])?.label ||
        '1'
      );
    }
    return `${filters.categories.length} selected`;
  }, [filters.categories]);

  const resultModeTriggerLabel = filters.showOnlyErrors
    ? 'Errors only'
    : 'All results';

  return (
    <div className="space-y-2 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by user, action, or message..."
            value={filters.searchText}
            onChange={handleSearchChange}
            className="pl-10 h-9"
          />
          {filters.searchText && (
            <button
              type="button"
              onClick={() => onFilterChange({ searchText: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <SearchDropdownMultiSelect
          items={userDropdownItems}
          selectedItems={selectedUserIds}
          onSelectionChange={handleUserChange}
          placeholder="Search users..."
          triggerLabel={userTriggerLabel}
          className="w-[200px] shrink-0"
        />

        <FilterDropdown
          name="Status"
          items={SEVERITY_OPTIONS}
          selectedItems={filters.severity}
          onSelectionChange={handleSeverityChange}
          onReset={() => onFilterChange({ severity: [] })}
          placeholder="Search status..."
          triggerLabel={statusTriggerLabel}
          className="w-[148px] shrink-0"
          classNameOptions="w-[260px]"
          showActions
          applyText="Apply"
          resetText="Reset"
        />

        <FilterDropdown
          name="Category"
          items={CATEGORY_OPTIONS}
          selectedItems={filters.categories}
          onSelectionChange={handleCategoryChange}
          onReset={() => onFilterChange({ categories: [] })}
          placeholder="Search categories..."
          triggerLabel={categoryTriggerLabel}
          className="w-[168px] shrink-0"
          classNameOptions="w-[260px]"
          showActions
          applyText="Apply"
          resetText="Reset"
        />

        <SearchDropdownSingleSelect
          name="Results"
          items={RESULT_MODE_OPTIONS}
          selectedItem={filters.showOnlyErrors ? 'errors' : 'all'}
          onSelectionChange={handleResultModeChange}
          placeholder="Search..."
          triggerLabel={resultModeTriggerLabel}
          className="w-[140px] shrink-0"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-muted-foreground h-9 shrink-0 px-2"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>

          {filters.searchText && (
            <Badge variant="secondary" className="gap-1">
              Search: "{filters.searchText}"
              <button
                type="button"
                onClick={() => onFilterChange({ searchText: '' })}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.users.map((userId) => {
            const user = users.find((u) => u.id === userId);
            return (
              <Badge key={userId} variant="secondary" className="gap-1">
                {user?.name || `User #${userId}`}
                <button
                  type="button"
                  onClick={() =>
                    onFilterChange({
                      users: filters.users.filter((id) => id !== userId),
                    })
                  }
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}

          {filters.severity.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1">
              {SEVERITY_CONFIGS[s].label}
              <button type="button" onClick={() => handleSeverityToggle(s)}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {filters.categories.map((c) => {
            const config = CATEGORY_CONFIGS.find((cat) => cat.id === c);
            return (
              <Badge key={c} variant="secondary" className="gap-1">
                {config?.label || c}
                <button type="button" onClick={() => handleCategoryToggle(c)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}

          {filters.showOnlyErrors && (
            <Badge
              variant="secondary"
              className="gap-1.5 font-normal text-red-700 bg-red-50 border border-red-200"
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              Errors only
              <button
                type="button"
                aria-label="Clear errors-only filter"
                onClick={() => onFilterChange({ showOnlyErrors: false })}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          <span className="text-sm text-muted-foreground ml-auto sm:ml-2">
            {totalResults.toLocaleString()} log
            {totalResults !== 1 ? 's' : ''} loaded
            {hasMore && (
              <span className="text-blue-600"> (scroll for more)</span>
            )}
          </span>
        </div>
      )}

      {!hasActiveFilters && (
        <div className="flex justify-end">
          <span className="text-sm text-muted-foreground">
            {totalResults.toLocaleString()} log
            {totalResults !== 1 ? 's' : ''} loaded
            {hasMore && (
              <span className="text-blue-600"> (scroll for more)</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
